import { all, checklist, ctl, ctlPart, field, has, is, test, ticks, unmapped, val, yn } from './helpers.js';
/**
 * Pieces both Chubb Cyber ERM forms share (revenue below and above RM1 billion): the checklists and
 * supplements are worded the same, only numbered differently.
 */
export const SEGMENTATION = [
    ['vlan', 'VLAN'],
    ['host_firewalls', 'Host-based firewalls'],
    ['acl', 'Firewall configuration (access control list)'],
    ['air_gap', 'Air gap'],
    ['sdn', 'Software defined networking'],
    ['least_privilege', 'Least privilege access controls'],
];
export const REMOTE_METHODS = [
    ['vpn', 'VPN'],
    ['mfa', 'Multi-factor authentication'],
    ['sso_mfa', 'SSO via MFA'],
    ['ztna', 'Zero trust network access'],
    ['encryption', 'Traffic encryption'],
];
export const RDP_USE = { not_used: 'RDP is not used at all', internal_only: 'Internal use only', remote_access: 'Used for remote access', other: 'Used in another capacity' }; // prettier-ignore
export const RDP_PROTECTIONS = [
    ['vpn', 'VPN'],
    ['mfa', 'Multi-factor authentication'],
    ['nla', 'Network Level Authentication'],
    ['honeypots', 'RDP honeypots'],
];
export const SCAN_CADENCE = { continuous: 'Continuously', monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Yearly', never: 'Never' }; // prettier-ignore
/** How email is secured. The MFA box is on the smaller form's list; the larger form asks it separately. */
export const emailChecks = (withMfa) => [
    ...(withMfa ? [['mfa', 'MFA required for webmail or cloud-hosted email', ctl('IA-02')]] : []), // prettier-ignore
    ['external_tag', 'Emails from outside tagged “External”', ctl('EH-07')],
    ['spf', 'SPF enforced', ctl('EH-06')],
    ['dkim', 'DKIM enforced', ctl('EH-06')],
    ['gateway', 'Secure email gateway', ctl('EH-05')],
    ['malware_scan', 'Incoming email scanned for malware', ctl('EH-05')],
    ['quarantine', 'Suspicious email quarantined automatically', has('UW-EMAIL-EXTRAS', 'quarantine')],
    ['secure_send', 'Sensitive external email sent securely', has('UW-EMAIL-EXTRAS', 'secure_send')],
    ['sandboxing', 'Attachments sandboxed', has('UW-EMAIL-EXTRAS', 'sandboxing')],
    ['macros', 'Office macros disabled by default', ctl('EP-07')],
    ['phishing_training', 'Staff trained on phishing and social engineering', ctl('EH-02')],
    ['other', 'Other'],
];
export const backupProtections = () => [
    ['immutable', 'Immutable or WORM backups', has('UW-BACKUP-PROTECTIONS', 'immutable')],
    ['offline', 'Offline or air-gapped backups', has('UW-BACKUP-PROTECTIONS', 'offline')],
    ['separate_account', 'Separate privileged account outside the directory', ctl('BR-04')],
    ['mfa', 'Access to backups needs MFA', ctl('BR-04')],
    ['encrypted', 'Backups encrypted', ctl('BR-07')],
    [
        'cloud_segmented',
        'Cloud backups kept apart from the network',
        has('UW-BACKUP-PROTECTIONS', 'cloud_segmented'),
    ],
    ['other', 'Other'],
];
/** Monitoring tools. EDR and protective DNS come from the controls; the rest from the client's list. */
export const monitoringChecks = (withThreatIntel) => [
    ['ids', 'Intrusion detection system', has('UW-MONITORING', 'ids')],
    ['ips', 'Intrusion prevention system', has('UW-MONITORING', 'ips')],
    [
        'antimalware',
        'Next-generation anti-malware with heuristic analysis',
        has('UW-MONITORING', 'antimalware'),
    ],
    ['web_filtering', 'URL or web filtering', has('UW-MONITORING', 'web_filtering')],
    ['isolation', 'Application isolation and containment', has('UW-MONITORING', 'isolation')],
    ['advanced_endpoint', 'Advanced endpoint protection', has('UW-MONITORING', 'advanced_endpoint')],
    ['edr', 'Endpoint detection and response (EDR)', ctl('EP-01')],
    ['mdr', 'Managed detection and response (MDR)', has('UW-MONITORING', 'mdr')],
    ['xdr', 'Extended detection and response (XDR)', has('UW-MONITORING', 'xdr')],
    ['manual_log_review', 'Manual log reviews', has('UW-MONITORING', 'manual_log_review')],
    ['siem', 'SIEM tool', has('UW-MONITORING', 'siem')],
    ['soc', 'Security operations centre', has('UW-MONITORING', 'soc')],
    ['soar', 'SOAR solution', has('UW-MONITORING', 'soar')],
    ['managed_firewall', 'Managed firewall service', has('UW-MONITORING', 'managed_firewall')],
    ['protective_dns', 'Protective DNS service', ctl('NS-08')],
    ...(withThreatIntel ? [['threat_intel', 'Threat intelligence sources or services', has('UW-MONITORING', 'threat_intel')]] : []), // prettier-ignore
    ['other', 'Other monitoring tools or services'],
];
export const VENDOR_CHECKS = [
    ['infosec_cert', 'Information security certification review'],
    ['resilience_cert', 'Business resilience certification review'],
    ['pentest', 'Penetration testing'],
    ['rating_service', 'Cyber security rating service'],
    ['backup_review', 'Review of the vendor’s backup procedures'],
    ['sla', 'Service level agreement assessment'],
    ['mfa_review', 'Multi-factor authentication review'],
    ['dpia', 'Data protection impact assessment'],
    ['dpa', 'Data protection agreements in contracts'],
];
export const RECOURSE = { never: 'Never or infrequently', sometimes: 'Sometimes', mostly: 'Always or most of the time' }; // prettier-ignore
export const cloudControls = () => [
    ['casb', 'Cloud access security broker (CASB)', has('UW-CLOUD-CONTROLS', 'casb')],
    ['sase', 'Secure access service edge (SASE)', has('UW-CLOUD-CONTROLS', 'sase')],
    ['ztna', 'Zero trust network access', has('UW-CLOUD-CONTROLS', 'ztna')],
    ['sso', 'Single sign-on for cloud services', ctl('IA-15')],
    ['mfa_critical', 'MFA for business-critical cloud apps', has('UW-CLOUD-CONTROLS', 'mfa_critical')],
    ['mfa_other', 'MFA for other cloud apps', has('UW-CLOUD-CONTROLS', 'mfa_other')],
];
/** Whether the business uses cloud services at all. */
export const USES_CLOUD = [is('P-IAAS', 'yes'), test('Q021', 'in', ['microsoft_365', 'google_workspace'])];
export const LOSSES = [
    ['data_breach', 'Data breach'],
    ['cyber_incident', 'Malicious cyber incident against you'],
    ['system_failure', 'System failure event'],
    ['media_claim', 'Media claim'],
    ['regulatory_action', 'Regulatory action over data or system security'],
    ['supplier_breach', 'Data breach at a third-party provider'],
    ['supplier_incident', 'Cyber incident affecting a third-party provider'],
];
/** Media: the same five questions on both forms. */
export const mediaFields = (ref) => [
    yn('trademarks_screened', ref('1'), 'Legal counsel has screened trademarks, domain names and metatags', has('UW-MEDIA', 'trademarks_screened')), // prettier-ignore
    yn('releases_obtained', ref('2'), 'Written permission obtained from outside content providers', has('UW-MEDIA', 'releases_obtained')), // prettier-ignore
    yn('legal_review', ref('3'), 'Legal counsel reviews content before publication or after a complaint', has('UW-MEDIA', 'legal_review')), // prettier-ignore
    yn('outsourced_content', ref('4'), 'Outside agencies create or manage content', has('UW-MEDIA', 'outsourced_content')), // prettier-ignore
    yn('indemnities', ref('4a'), 'Those agencies give indemnities or hold-harmless agreements', has('UW-MEDIA', 'indemnities'), { if: { field: 'outsourced_content', in: ['yes'] } }), // prettier-ignore
    yn('policies_reviewed', ref('5'), 'Privacy policy and terms reviewed by counsel', has('UW-MEDIA', 'policies_reviewed')), // prettier-ignore
];
export const lossFields = (ref) => [
    checklist('losses', ref('1'), 'Events in the past five years', [
        ...ticks('UW-LOSSES', LOSSES),
        ['none', 'None'],
    ]),
    field('loss_detail', ref('1a'), 'Each event: what happened and when, its cost, and what changed', 'table', val('UW-LOSS-DETAIL'), { if: { field: 'losses', in: LOSSES.map(([k]) => k) } }), // prettier-ignore
    yn('circumstances', ref('2'), 'Aware of anything that could give rise to a claim', is('UW-CIRCUMSTANCES')),
    field('circumstances_detail', ref('2a'), 'Details', 'text', val('UW-CIRCUMSTANCES-DETAIL'), { if: { field: 'circumstances', in: ['yes'] } }), // prettier-ignore
];
export const biometricFields = (ref) => [
    yn('bio_employees', ref('1a'), 'Collected from employees', has('UW-BIOMETRIC-FROM', 'employees')),
    yn('bio_contractors', ref('1b'), 'Collected from service providers or contractors', has('UW-BIOMETRIC-FROM', 'contractors')), // prettier-ignore
    yn('bio_customers', ref('1c'), 'Collected from customers', has('UW-BIOMETRIC-FROM', 'customers')),
    yn('bio_release', ref('2a'), 'Written consent and release from each employee', is('UW-BIOMETRIC-CONSENT'), { if: { field: 'bio_employees', in: ['yes'] } }), // prettier-ignore
    yn('bio_arbitration', ref('2b'), 'Employees sign an arbitration agreement with a class action waiver', unmapped(), { if: { field: 'bio_employees', in: ['yes'] } }), // prettier-ignore
    yn('bio_policy', ref('3'), 'Written biometric privacy policy covering retention and destruction', is('UW-BIOMETRIC-POLICY')),
    yn('bio_explicit_consent', ref('4'), 'Explicit written consent always obtained', is('UW-BIOMETRIC-CONSENT')),
    field('bio_since', ref('5'), 'When biometric collection started', 'text', unmapped()),
    field('bio_consent_since', ref('6'), 'How long explicit written consent has been required', 'text', unmapped()),
    field('bio_records', ref('7'), 'Biometric records held', 'number', val('UW-BIOMETRIC-RECORDS')),
];
export const BIOMETRIC_APPLIES = [is('UW-BIOMETRIC')];
/** OT applies to manufacturers and logistics firms with connected machines, and to energy and utilities. */
export const OT_APPLIES = [
    {
        from: 'any',
        of: [is('Q031'), { from: 'detail', key: 'industry', op: 'in', value: ['energy', 'utilities'] }],
    },
];
export const otRemote = val('Q044', { on_site: 'no', on_request: 'yes', always_on: 'yes' });
export const otMonitored = [ctl('LD-12'), ctl('LD-19')];
export const otBackups = [ctl('BR-14'), ctl('BR-22')];
export const RETAIL_APPLIES = [
    {
        from: 'any',
        of: [{ from: 'detail', key: 'industry', op: 'eq', value: 'retail_ecommerce' }, is('UW-PCI')],
    },
];
export const retailFields = (ref) => [
    yn('pos_segregated', ref('1'), 'Point of sale and transaction processing separated from other networks', ctl('NS-14')), // prettier-ignore
    field('pos_patching', ref('2'), 'Patch process and cadence for point-of-sale software', 'text', unmapped()),
    field('emv_pct', ref('3'), 'Share of terminals that meet EMV chip standards', 'percentage', val('UW-EMV-PCT')),
    field('payment_providers', ref('4'), 'Payment processing providers', 'text', val('UW-PAYMENT-PROVIDERS')),
    yn('pos_monitored', ref('5'), 'Point of sale protected by antimalware and monitored by security staff', is('UW-POS-MONITORED')), // prettier-ignore
    field('retail_commentary', ref('5'), 'Additional commentary', 'text', unmapped(), { required: false }),
    yn('franchise', ref('6'), 'Has franchisee locations or agreements', is('UW-FRANCHISE')),
    field('franchise_detail', ref('6a'), 'Who is responsible for cyber security at franchisees, and how', 'text', val('UW-FRANCHISE-DETAIL'), { if: { field: 'franchise', in: ['yes'] } }), // prettier-ignore
];
export const PROFESSIONAL_APPLIES = [{ from: 'detail', key: 'industry', op: 'eq', value: 'professional_services' }]; // prettier-ignore
export const professionalFields = (ref) => [
    yn('pi_insurance', ref('1'), 'Buys professional indemnity insurance', is('UW-PI-INSURANCE')),
    yn('pi_cyber_exclusion', ref('2'), 'That policy has a cyber exclusion', is('UW-PI-CYBER-EXCLUSION'), { if: { field: 'pi_insurance', in: ['yes'] } }), // prettier-ignore
    yn('hosts_for_clients', ref('3'), 'Operates or hosts technology systems for its professional services', unmapped()),
    yn('client_responsible', ref('3a'), 'Data and systems of those services are the customer’s responsibility', unmapped(), { if: { field: 'hosts_for_clients', in: ['yes'] } }), // prettier-ignore
    yn('controls_apply_hosted', ref('3b'), 'This form’s controls also apply to the hosted systems', unmapped(), { if: { field: 'hosts_for_clients', in: ['yes'] } }), // prettier-ignore
];
/** Acquisitions in the last three years. */
export const acquisitionFields = (ref) => [
    field('acquisitions', ref('1'), 'Acquisitions in the past three years', 'number', val('UW-ACQUISITIONS')),
    field('acquisition_detail', ref('2'), 'Entities acquired, their size and dates', 'text', val('UW-ACQUISITION-DETAIL'), { na_when: test('UW-ACQUISITIONS', 'eq', 0) }), // prettier-ignore
    { key: 'acquisition_review', ref: ref('3'), label: 'When the cyber security of acquired entities is assessed', type: 'choice', options: [{ value: 'before', label: 'Before acquisition' }, { value: 'before_integration', label: 'After acquisition, before integration' }, { value: 'rarely', label: 'Rarely assessed' }, { value: 'other', label: 'Other' }], source: val('UW-ACQUISITION-REVIEW'), na_when: test('UW-ACQUISITIONS', 'eq', 0) }, // prettier-ignore
    field('acquisition_integration', ref('4'), 'Integration strategy, timelines and due diligence', 'text', unmapped(), { required: false }), // prettier-ignore
];
/** End-of-life systems: a–d are only meaningful when there are some. */
export const eolFields = (ref) => {
    const none = is('UW-EOL', 'no');
    return [
        yn('eol_critical', ref('a'), 'Any of them business-critical', has('UW-EOL-FACTS', 'business_critical'), { na_when: none }), // prettier-ignore
        yn('eol_sensitive', ref('b'), 'Sensitive personal or confidential data on them', has('UW-EOL-FACTS', 'sensitive_data'), { na_when: none }), // prettier-ignore
        yn('eol_no_internet', ref('c'), 'Restricted from internet access', has('UW-EOL-FACTS', 'internet_restricted'), { na_when: none }), // prettier-ignore
        yn('eol_segregated', ref('d'), 'Segregated from the rest of the network', has('UW-EOL-FACTS', 'segregated'), { na_when: none }), // prettier-ignore
        field('eol_detail', ref('e–f'), 'Which unsupported systems, their use and number, and decommissioning plans', 'text', val('UW-EOL-DETAIL'), { na_when: none }), // prettier-ignore
        field('eol_mitigation', ref('g'), 'Other controls limiting lateral movement from them', 'text', unmapped(), { required: false, na_when: none }), // prettier-ignore
    ];
};
export const patchDays = (ref) => [
    field('patch_days_low', ref, 'Target days to patch: low', 'number', val('UW-PATCH-DAYS-LOW')),
    field('patch_days_medium', ref, 'Target days to patch: medium', 'number', val('UW-PATCH-DAYS-MEDIUM')),
    field('patch_days_high', ref, 'Target days to patch: high', 'number', val('UW-PATCH-DAYS-HIGH')),
    field('patch_days_critical', ref, 'Target days to patch: critical', 'number', val('UW-PATCH-DAYS-CRITICAL')),
];
/** Business continuity and recovery facts both forms ask in the same words. */
export const resilienceFields = (ref, from) => [
    yn('alternate_power', ref(from), 'Alternative power for mission-critical or revenue-generating equipment', has('UW-RESILIENCE', 'alternate_power')), // prettier-ignore
    yn('extra_bandwidth', ref(from + 1), 'Can get extra bandwidth from another supplier', has('UW-RESILIENCE', 'extra_bandwidth')), // prettier-ignore
    yn('generators_tested', ref(from + 2), 'Backup generators or dual power supplies used and tested', has('UW-RESILIENCE', 'generators_tested')), // prettier-ignore
];
/** A restore was tested within the year (the 90-day control counts it fully, 3–12 months partly). */
export const RESTORE_THIS_YEAR = ctlPart('BR-03');
export const RECOVERY_CHECKED = all(ctlPart('BR-03'), ctl('BR-13'));
/**
 * Answers likely to narrow Chubb's terms or lead to a decline (task 91): the controls cyber underwriters
 * look at first. They are the platform's indication for the broker, not Chubb's published criteria.
 */
const terms = (note, on) => ({ ...on, effect: 'terms', note }); // prettier-ignore
export const chubbBlocks = (emailMfa) => ({
    remote_methods: terms('Remote access without MFA: insurers often decline, or limit ransomware cover.', { lacks: ['mfa', 'sso_mfa'] }), // prettier-ignore
    remote_scope: terms('MFA on remote access doesn’t cover everyone who uses it.', { in: ['no', 'partial'] }),
    rdp_use: terms('Remote Desktop used for remote access is a common way in for ransomware.', { in: ['remote_access'] }), // prettier-ignore
    ...(emailMfa ? { email_security: terms('No MFA on webmail or cloud email.', { lacks: ['mfa'] }) } : {}),
    backup_protections: terms('No immutable, offline or separated cloud backups to recover from ransomware.', { lacks: ['immutable', 'offline', 'cloud_segmented'] }), // prettier-ignore
    monitoring: terms('No EDR, MDR or XDR on endpoints.', { lacks: ['edr', 'mdr', 'xdr'] }),
    eol_critical: terms('Business-critical systems that no longer get security updates.', { in: ['yes'] }),
});
//# sourceMappingURL=chubb-shared.js.map